import type { Job } from "../types";
import { jobBoundaryAcres } from "./jobAreaStats";

/** Heatmap attribute + weight math, ported from MC's HeatmapAttributeCalculator
 *  (design §3.6): percentile-based normalization (5th/95th clip) for the layer weights,
 *  and percentile-edged bins for the "<attr> Distribution" histogram. */

export type HeatmapAttributeKey = "daysLate" | "boundaryAcres";

export const HEATMAP_ATTRIBUTE_LABELS: Record<HeatmapAttributeKey, string> = {
	daysLate: "Days Late",
	boundaryAcres: "Boundary Acres",
};

/** A job's raw value for the attribute. MC parity: Days Late excludes zero/missing
 *  (`get('Late (days)') || undefined`); Boundary Acres always yields a number (zero
 *  included) — here geometry-first via jobBoundaryAcres, like the area stats. */
export function heatmapValue(job: Job, attribute: HeatmapAttributeKey): number | undefined {
	if (attribute === "daysLate") return job.daysLate || undefined;
	return jobBoundaryAcres(job);
}

/** Linear-interpolated percentile of a pre-sorted ascending array (MC calculatePercentile). */
function percentileOf(sortedValues: readonly number[], percentile: number): number {
	const index = (percentile / 100) * (sortedValues.length - 1);
	const lower = Math.floor(index);
	const upper = Math.ceil(index);
	const weight = index - lower;
	if (lower === upper) return sortedValues[lower];
	return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
}

/** Normalize raw values to [0, 1] weights, order-preserving. MC rules: empty → [];
 *  all-equal → 0.5 each; otherwise clip to the 5th–95th percentile band (min/max when
 *  that band collapses) and scale linearly. */
export function heatmapWeights(values: readonly number[]): number[] {
	if (values.length === 0) return [];

	const sorted = [...values].sort((a, b) => a - b);
	const minValue = sorted[0];
	const maxValue = sorted[sorted.length - 1];
	if (maxValue === minValue) return values.map(() => 0.5);

	const p5 = percentileOf(sorted, 5);
	const p95 = percentileOf(sorted, 95);
	const usePercentiles = p95 - p5 > 0;
	const lowerBound = usePercentiles ? p5 : minValue;
	const upperBound = usePercentiles ? p95 : maxValue;
	const range = upperBound - lowerBound;

	return values.map((value) => {
		const clipped = Math.max(lowerBound, Math.min(upperBound, value));
		return (clipped - lowerBound) / range;
	});
}

export interface HeatmapBin {
	count: number;
	minValue: number;
	maxValue: number;
}

/** Histogram bins with percentile-based edges (MC calculateBinEdges + its counting loop:
 *  first matching [edge, nextEdge] wins; the exact maximum lands in the last bin).
 *  Degenerate input (empty, or all values equal) → [] — the histogram renders
 *  "No data available". */
export function heatmapBins(values: readonly number[], binCount: number): HeatmapBin[] {
	if (values.length === 0) return [];

	const sorted = [...values].sort((a, b) => a - b);
	if (sorted[0] === sorted[sorted.length - 1]) return [];

	const edges: number[] = [sorted[0]];
	for (let i = 1; i < binCount; i++) {
		edges.push(percentileOf(sorted, (i / binCount) * 100));
	}
	edges.push(sorted[sorted.length - 1]);

	const bins: HeatmapBin[] = Array.from({ length: binCount }, (_, i) => ({
		count: 0,
		minValue: edges[i],
		maxValue: edges[i + 1],
	}));

	for (const value of values) {
		let binIndex = 0;
		for (let i = 0; i < binCount; i++) {
			if (value >= edges[i] && value <= edges[i + 1]) {
				binIndex = i;
				break;
			}
		}
		if (value === edges[binCount]) binIndex = binCount - 1;
		bins[binIndex].count++;
	}

	return bins;
}
